import React, { useState } from 'react'
import { Alert, Box, Button, CircularProgress, TextField } from '@mui/material'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import { supabase } from '../../supabaseClient'

// Controlled message composer with an AI "Polish" button and a Send button.
// The parent owns the text (value/onChange) and performs the send in onSend,
// returning an error string (or null on success). Reused by the parent portal
// and the staff parent-reply dialog.

interface Props {
  value:        string
  onChange:     (v: string) => void
  onSend:       (text: string) => Promise<string | null>
  placeholder?: string
  sendLabel?:   string
  disabled?:    boolean
  /** Show the "Polish with AI" button. Off for parent-facing composers. */
  showPolish?:  boolean
  /** Textarea height in rows (default 3). */
  rows?:        number
  /** Extra buttons (e.g. "Draft with AI") rendered left of Polish. */
  leftSlot?:    React.ReactNode
}

export default function PolishComposer({
  value, onChange, onSend, placeholder, sendLabel = 'Send', disabled, showPolish = true, rows = 3, leftSlot,
}: Props) {
  const [sending,   setSending]   = useState(false)
  const [polishing, setPolishing] = useState(false)
  const [error,     setError]     = useState<string | null>(null)
  const [ok,        setOk]        = useState<string | null>(null)

  const trimmed = value.trim()

  async function polish() {
    if (polishing || sending || trimmed.length === 0) return
    setPolishing(true); setError(null); setOk(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('polish-message', {
        body: { text: trimmed },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      if (data?.polished) onChange(data.polished)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not polish — please try again.')
    } finally {
      setPolishing(false)
    }
  }

  async function send() {
    if (sending || polishing || trimmed.length === 0) return
    setSending(true); setError(null); setOk(null)
    const err = await onSend(trimmed)
    if (err) setError(err)
    else { onChange(''); setOk('Sent.') }
    setSending(false)
  }

  const busy = disabled || sending || polishing

  return (
    <Box>
      <TextField
        fullWidth multiline rows={rows} size="small"
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={busy}
        sx={{ mb: 1 }}
      />
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {ok    && <Alert severity="success" sx={{ mb: 1 }}>{ok}</Alert>}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {leftSlot}
        {showPolish && (
          <Button
            size="small" variant="text"
            startIcon={polishing ? <CircularProgress size={14} /> : <AutoFixHighIcon fontSize="small" />}
            onClick={polish}
            disabled={busy || trimmed.length === 0}
            sx={{ ml: 'auto' }}
          >
            {polishing ? 'Polishing…' : 'Polish with AI'}
          </Button>
        )}
        <Button
          size="small" variant="contained"
          onClick={send}
          disabled={busy || trimmed.length === 0}
          sx={{ ml: showPolish ? 0 : 'auto' }}
        >
          {sending ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />Sending…</> : sendLabel}
        </Button>
      </Box>
    </Box>
  )
}
